import { useCallback, useEffect, useMemo, useRef, createContext, useContext } from 'react';
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  getSmoothStepPath,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import { Pencil } from 'lucide-react';
import '@xyflow/react/dist/style.css';
import './endToEndProcessMap.css';
import {
  defaultProcessPosition,
  processNodeId,
} from './endToEndMapStorage';
import { emptyEndToEndMap, mapFromCanvas } from './endToEndMapApi';

const defaultEdgeOptions = {
  type: 'endToEndProcess',
  animated: true,
  interactionWidth: 20,
  style: { stroke: '#0ea5e9', strokeWidth: 2.25 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#0ea5e9',
    width: 18,
    height: 18,
  },
};

const TILE_WIDTH = 320;
const TILE_HEIGHT = 220;
/** Sit edit on the path, near the outgoing end so A→B and B→A do not stack. */
const EDIT_BUTTON_PATH_RATIO = 0.2;

const EndToEndEdgeEditContext = createContext(null);

function pointAlongPath(path, ratio = EDIT_BUTTON_PATH_RATIO) {
  if (typeof document === 'undefined' || !path) return null;
  try {
    const element = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    element.setAttribute('d', path);
    const length = element.getTotalLength();
    if (!Number.isFinite(length) || length <= 0) return null;
    const clamped = Math.min(0.85, Math.max(0.08, ratio));
    const point = element.getPointAtLength(length * clamped);
    return { x: point.x, y: point.y };
  } catch {
    return null;
  }
}

function EndToEndProcessEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
}) {
  const onEditConnection = useContext(EndToEndEdgeEditContext);
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 18,
  });
  const alongPath = pointAlongPath(edgePath, EDIT_BUTTON_PATH_RATIO);
  const editX = alongPath?.x ?? labelX;
  const editY = alongPath?.y ?? labelY;

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <button
          type="button"
          className="end-to-end-edge-edit nodrag nopan"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${editX}px, ${editY}px)`,
            pointerEvents: 'all',
          }}
          onClick={(event) => {
            event.stopPropagation();
            onEditConnection?.({ source, target });
          }}
          aria-label="Edit process connection links"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </EdgeLabelRenderer>
    </>
  );
}

function sourceLabel(source) {
  const value = String(source || '').trim().toLowerCase();
  if (value === 'ai_assisted') return 'AI extracted';
  if (value === 'batch_import') return 'Batch import';
  if (value === 'manual') return 'Manual';
  return source || 'Unknown';
}

function EndToEndProcessNode({ data }) {
  const process = data?.process || {};
  const name = process.name || 'Untitled process';
  const processCode = process.process_id || process.id || '';
  const hasFlowchart = Boolean(process.has_flowchart);

  return (
    <div className="group relative" style={{ width: TILE_WIDTH, height: TILE_HEIGHT }}>
      <Handle type="target" position={Position.Left} id="left" className="end-to-end-handle" />
      <Handle type="source" position={Position.Right} id="right" className="end-to-end-handle" />

      <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/5 transition-shadow duration-200 group-hover:shadow-[0_22px_50px_rgba(14,165,233,0.16)]">
        <div className="h-1.5 shrink-0 bg-gradient-to-r from-sky-600 via-cyan-500 to-indigo-500" />
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-3 pt-3">
          <div className="flex h-8 shrink-0 items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] leading-none text-slate-400">
                Process
              </span>
            </div>
            <span className="shrink-0 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium leading-none text-slate-600 ring-1 ring-slate-200">
              {sourceLabel(process.source)}
            </span>
          </div>

          <p className="mt-3 h-10 shrink-0 overflow-hidden text-[15px] font-semibold leading-5 text-slate-900 line-clamp-2">
            {name}
          </p>
          <p className="mt-1 h-5 shrink-0 overflow-hidden text-xs font-medium leading-5 tracking-wide text-slate-400">
            {processCode || ' '}
          </p>
          <p className="mt-2 h-8 shrink-0 overflow-hidden text-xs leading-4 text-slate-500 line-clamp-2">
            {process.description || ' '}
          </p>

          <div className="mt-auto flex h-6 shrink-0 items-center justify-between gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium leading-none ${
                hasFlowchart
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                  : 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${hasFlowchart ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              {hasFlowchart ? 'Flowchart ready' : 'No flowchart yet'}
            </span>
            <span className="text-[11px] font-medium leading-none text-sky-700">Click to open</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const nodeTypes = { process: EndToEndProcessNode };
const edgeTypes = { endToEndProcess: EndToEndProcessEdge };

function EndToEndProcessMapInner({
  processes,
  savedMap,
  onMapChange,
  onProcessClick,
  onConnectProcesses,
  onEditConnection,
  onTilesMoved,
}) {
  const list = Array.isArray(processes) ? processes : [];
  const processKey = useMemo(
    () =>
      list
        .map((process) => {
          const id = processNodeId(process);
          if (!id) return '';
          return `${id}:${process?.has_flowchart ? 1 : 0}`;
        })
        .filter(Boolean)
        .join(','),
    [list]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const hydratedRef = useRef(false);
  const listRef = useRef(list);
  const onMapChangeRef = useRef(onMapChange);
  const onConnectProcessesRef = useRef(onConnectProcesses);
  const onEditConnectionRef = useRef(onEditConnection);
  const onTilesMovedRef = useRef(onTilesMoved);
  const edgesRef = useRef(edges);
  const nodesRef = useRef(nodes);
  listRef.current = list;
  onMapChangeRef.current = onMapChange;
  onConnectProcessesRef.current = onConnectProcesses;
  onEditConnectionRef.current = onEditConnection;
  onTilesMovedRef.current = onTilesMoved;
  edgesRef.current = edges;
  nodesRef.current = nodes;

  useEffect(() => {
    const currentList = listRef.current;
    const saved = savedMap && typeof savedMap === 'object' ? savedMap : emptyEndToEndMap();
    const nextNodes = currentList
      .map((process, index) => {
        const id = processNodeId(process);
        if (!id) return null;
        const savedPosition = saved.positions?.[id];
        return {
          id,
          type: 'process',
          position:
            savedPosition && Number.isFinite(Number(savedPosition.x)) && Number.isFinite(Number(savedPosition.y))
              ? { x: Number(savedPosition.x), y: Number(savedPosition.y) }
              : defaultProcessPosition(index),
          data: { process },
        };
      })
      .filter(Boolean);

    const ids = new Set(nextNodes.map((node) => node.id));
    const nextEdges = (saved.edges || [])
      .filter((edge) => ids.has(edge.source) && ids.has(edge.target) && edge.source !== edge.target)
      .map((edge, index) => ({
        ...defaultEdgeOptions,
        id: edge.id || `e2e-${edge.source}-${edge.target}-${index}`,
        source: edge.source,
        target: edge.target,
        sourceHandle: 'right',
        targetHandle: 'left',
      }));

    setNodes(nextNodes);
    setEdges(nextEdges);
    hydratedRef.current = true;
    onMapChangeRef.current?.(mapFromCanvas(nextNodes, nextEdges));
  }, [processKey, savedMap, setEdges, setNodes]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    onMapChangeRef.current?.(mapFromCanvas(nodes, edges));
  }, [nodes, edges]);

  const findProcessByNodeId = useCallback((nodeId) => {
    const id = String(nodeId || '').trim();
    if (!id) return null;
    return listRef.current.find((process) => processNodeId(process) === id) || null;
  }, []);

  const onConnect = useCallback(
    (connection) => {
      if (!connection?.source || !connection?.target || connection.source === connection.target) {
        return;
      }
      // Subprocess links are always right (source) → left (target).
      if (connection.sourceHandle !== 'right' || connection.targetHandle !== 'left') {
        return;
      }
      const sourceProcess = findProcessByNodeId(connection.source);
      const targetProcess = findProcessByNodeId(connection.target);
      if (!sourceProcess || !targetProcess) return;
      onConnectProcessesRef.current?.({ sourceProcess, targetProcess });
    },
    [findProcessByNodeId]
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      const rest = (Array.isArray(changes) ? changes : []).filter((change) => change?.type !== 'remove');
      if (rest.length > 0) onEdgesChange(rest);
    },
    [onEdgesChange]
  );

  const handleNodeClick = useCallback(
    (_event, node) => {
      if (node?.data?.process) onProcessClick?.(node.data.process);
    },
    [onProcessClick]
  );

  const handleNodeDragStop = useCallback((_event, node, draggedNodes) => {
    let currentNodes =
      Array.isArray(draggedNodes) && draggedNodes.length > 0 ? draggedNodes : nodesRef.current;
    if (node?.id && node?.position && Array.isArray(currentNodes)) {
      currentNodes = currentNodes.map((item) =>
        item.id === node.id ? { ...item, position: node.position } : item
      );
    }
    onMapChangeRef.current?.(mapFromCanvas(currentNodes, edgesRef.current));
    onTilesMovedRef.current?.();
  }, []);

  const handleEditConnection = useCallback(
    ({ source, target }) => {
      const sourceProcess = findProcessByNodeId(source);
      const targetProcess = findProcessByNodeId(target);
      if (!sourceProcess || !targetProcess) return;
      onEditConnectionRef.current?.({ sourceProcess, targetProcess });
    },
    [findProcessByNodeId]
  );

  if (list.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/80 px-8 py-12 text-center text-sm text-slate-500 shadow-sm">
          No processes in this cycle yet.
        </div>
      </div>
    );
  }

  return (
    <div className="end-to-end-map h-full w-full">
      <EndToEndEdgeEditContext.Provider value={handleEditConnection}>
        <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeDragStop={handleNodeDragStop}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.18, minZoom: 0.45, maxZoom: 1 }}
        minZoom={0.3}
        maxZoom={1.4}
        nodesDraggable
        nodesConnectable
        elementsSelectable
        deleteKeyCode={null}
        proOptions={{ hideAttribution: true }}
        connectionRadius={40}
      >
        <Background color="#cbd5e1" gap={22} size={1.2} />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          nodeColor="#0ea5e9"
          maskColor="rgba(15, 23, 42, 0.08)"
        />
        </ReactFlow>
      </EndToEndEdgeEditContext.Provider>
    </div>
  );
}

export default function CycleEndToEndProcessChart({
  processes,
  savedMap,
  onMapChange,
  onProcessClick,
  onConnectProcesses,
  onEditConnection,
  onTilesMoved,
}) {
  return (
    <ReactFlowProvider>
      <EndToEndProcessMapInner
        processes={processes}
        savedMap={savedMap}
        onMapChange={onMapChange}
        onProcessClick={onProcessClick}
        onConnectProcesses={onConnectProcesses}
        onEditConnection={onEditConnection}
        onTilesMoved={onTilesMoved}
      />
    </ReactFlowProvider>
  );
}
